import { Storage } from "../../_internal/storage/index.js";
import { memoize } from "es-toolkit";

//#region src/module/local-storage/index.ts
let _GLOBAL_PREFIX = "";
function convertToStorage(value, nullable) {
	if (nullable) {
		if (typeof value === "undefined") return "undefined";
		if (value === null) return "null";
	}
	return value;
}
function convertFromStorage(value, nullable) {
	if (nullable) {
		if (value === "undefined") return void 0;
		if (value === "null") return null;
	}
	return value;
}
var LocalStorage = class extends Storage {
	_prefix = "";
	constructor(fields, config) {
		super(fields);
		this._prefix = config?.prefix ?? "";
	}
	_getIdentifier(name) {
		const field = this._fields[name];
		const key = String(field.key ?? name);
		return `${_GLOBAL_PREFIX}${this._prefix}${key}`;
	}
	StorageProvider = new class {
		parent;
		constructor(parent) {
			this.parent = parent;
		}
		getRaw = memoize((name) => {
			const res = window.localStorage.getItem(this.parent._getIdentifier(name));
			if (!res) return;
			const [cachedAtTime, value, nullable] = JSON.parse(res);
			return [
				cachedAtTime,
				value,
				nullable
			];
		});
		shouldRemove(name) {
			const raw = (this.getRaw(name));
			if (!raw) return;
			const [cachedAtTime] = raw;
			const { TTL } = this.parent._fields[name];
			const expiresAt = typeof TTL === "number" && typeof cachedAtTime === "number" ? TTL * 1e3 + cachedAtTime : void 0;
			const now = (Date.now());
			if (typeof expiresAt === "number" && now >= expiresAt) return true;
			return false;
		}
		getMemoized = memoize((name) => {
			const raw = this.getRaw(name);
			if (!raw) return;
			const [, value, nullable] = raw;
			return convertFromStorage(value, !!nullable);
		});
		get(name) {
			const raw = (this.getRaw(name));
			if (!raw) return;
			if (this.shouldRemove(name)) {
				this.parent.remove(name);
				return;
			}
			return this.getMemoized(name);
		}
		set = (name, value) => {
			const existingRawValue = this.getRaw(name);
			const field = this.parent._fields[name];
			const convertedValue = convertToStorage(value, field.nullable);
			const valueArr = [Date.now(), convertedValue];
			if (field.nullable) valueArr.push(1);
			window.localStorage.setItem(this.parent._getIdentifier(name), JSON.stringify(valueArr));
			this.getRaw.cache.delete(name);
			if (existingRawValue && JSON.stringify(existingRawValue[1]) !== JSON.stringify(convertedValue)) this.getMemoized.cache.delete(name);
		};
		remove = (name) => {
			window.localStorage.removeItem(this.parent._getIdentifier(name));
			this.getRaw.cache.delete(name);
			this.getMemoized.cache.delete(name);
		};
		has = (name) => {
			const res = window.localStorage.getItem(this.parent._getIdentifier(name));
			return res !== null;
		};
	}(this);
	/**
	* Set prefix for all keys using this LocalStorage provider
	*/
	static setGlobalPrefix(prefix) {
		_GLOBAL_PREFIX = prefix;
	}
};

//#endregion
export { LocalStorage };